import React, { useEffect, useRef, useState } from 'react';
import { useLocales, useTheme } from '../../../hooks';
import Button from '../../shared/Button';
import ShadowScroller from '../../shared/ShadowScroller';
import { AssetUploader } from '../AssetUploader';
import { AssetResponse } from '../../../API';
import { ListItemButton, Popover, Tooltip, Typography } from '@mui/material';
import { ImageSearch, Warning } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import Repeat from '../../shared/Repeat';
import { AspectRatio } from '../../../utils/aspectRatios';
import { Accept } from 'react-dropzone';
import { useData } from '../../../data-layer';
import { ShadowList } from '../../shared/Virtuoso';
import { AssetCard } from './AssetCard';
import { AssetSkeleton } from './AssetSkeleton';
import TextField from '../../shared/TextField';
import { markAsRequired } from '../../../utils/formHelpers';
import { AssetTypesEnum } from '../../../utils/assetTypes';
import { assetBrowserTestIds } from '../../shared/TestsIds';
import { DEFAULT_PAGINATION_LIMIT } from '../../../utils/appDefaults';

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  elementRoot: {
    display: 'flex'
  },
  formLabelError: {
    color: theme.palette.error.main
  },
  root: {
    position: 'relative',
    width: '100%'
  },
  inline: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    height: '100%',
    minHeight: 44
  },
  popoverContainer: {
    background: theme.palette.background.paper,
    height: '45vh',
    minHeight: 300,
    width: 400,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3, 2, 2),
    display: 'flex',
    flexDirection: 'column'
  },
  assetRow: {
    padding: theme.spacing(1, 2)
  },
  assetsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2)
  }
}));

const ASSET_CARD_SIZE = 42;

type AssetBrowserProps = {
  assetId?: string;
  assetType: AssetTypesEnum;
  label?: string;
  required?: boolean;
  value?: string | AssetResponse | undefined;
  hasError?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  acceptedFileTypes?: Accept;
  useDarkBackground?: boolean;
  aspectRatios?: AspectRatio[];
  'data-testid'?: string;
};

function AssetBrowserComponent({
  assetType,
  assetId = assetType,
  label,
  required,
  value,
  hasError,
  onChange,
  acceptedFileTypes,
  useDarkBackground,
  aspectRatios,
  ...props
}: AssetBrowserProps): JSX.Element {
  const { cx, classes } = useStyles();
  const { t } = useLocales();
  const { formControlColor } = useTheme();
  const {
    assets: {
      hook: { searchAssets, getByFilePath }
    }
  } = useData();

  const [asset, setAsset] = useState<AssetResponse | undefined>();

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAsset, setIsLoadingAsset] = useState(false);

  const [assets, setAssets] = useState<AssetResponse[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [open, setOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [invalid, setInvalid] = useState(false);
  const popoverAnchorRef = useRef(null);
  const [editingAsset, setEditingAsset] = useState<AssetResponse | undefined>();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const pageRef = useRef(1);
  // Virtuoso can fire endReached several times before a re-render lands, so the guard has to be a
  // ref: reading a piece of state would let two calls through and append the same page twice.
  const isFetchingRef = useRef(false);

  // Opening the picker, switching its type, or typing a term all start again from page one.
  // Nothing is fetched while the picker is closed, and the term is applied by the API: this used
  // to load every asset of the type on mount so the search box could filter them in the browser.
  useEffect(() => {
    if (!open) {
      return;
    }

    loadFirstPage();
  }, [open, assetType, filter]);

  useEffect(() => {
    if (!asset || asset.filePath !== value) {
      loadAsset();
    }
  }, [value]);

  const loadAsset = async () => {
    if (value) {
      setIsLoadingAsset(true);
      let fetched = undefined;
      if (typeof value === 'string') {
        fetched = await getByFilePath(value);
      } else {
        fetched = value;
      }
      setAsset(fetched);
      setInvalid(!fetched);
      setIsLoadingAsset(false);
    }
  };

  const fetchPage = async (page: number) =>
    searchAssets({ page, limit: DEFAULT_PAGINATION_LIMIT, assetType, assetName: filter });

  const loadFirstPage = async () => {
    isFetchingRef.current = true;
    setIsLoading(true);
    try {
      const { results, meta } = await fetchPage(1);
      pageRef.current = 1;
      setAssets(results ?? []);
      setHasNextPage(!!meta?.hasNextPage);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  const loadNextPage = async () => {
    if (!hasNextPage || isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    try {
      const nextPage = pageRef.current + 1;
      const { results, meta } = await fetchPage(nextPage);
      pageRef.current = nextPage;
      setAssets((current) => [...current, ...(results ?? [])]);
      setHasNextPage(!!meta?.hasNextPage);
    } finally {
      isFetchingRef.current = false;
    }
  };

  const handleBrowseClick = () => {
    const isOpen = !open;
    setOpen(isOpen);
    if (isOpen) {
      // Focus the search input when opening the browser
      setTimeout(() => {
        searchInputRef.current?.focus();
      });
    }
  };

  const handleUploadClick = () => setIsUploadOpen(true);

  const handleAssetFormClose = () => {
    setIsUploadOpen(false);
    setEditingAsset(undefined);

    // The list is a locally held page now, so an asset that was just uploaded or renamed only
    // shows up if the page is fetched again.
    if (open) {
      loadFirstPage();
    }
  };

  const fireOnChange = (value: string | undefined) => {
    // mock out a fake change event to trigger the LocalizedInputCollection onChange event
    if (onChange) {
      onChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const selectAsset = (asset: AssetResponse) => {
    fireOnChange(asset.filePath);
  };

  const handleAssetSelect = (asset: AssetResponse) => {
    selectAsset(asset);
    setOpen(false);
  };

  const handleAssetUpload = (asset: AssetResponse, isEditing: boolean) => {
    setEditingAsset(undefined);
    if (!isEditing) {
      selectAsset(asset);
      setOpen(false);
    }
  };

  const handleClear = () => {
    setAsset(undefined);
    fireOnChange('');
  };

  return (
    <>
      <div data-testid={props['data-testid']} className={classes.container}>
        {label && (
          <Typography
            component="div"
            color="textSecondary"
            variant="body2"
            className={cx({ [classes.formLabelError]: hasError })}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            {required ? markAsRequired(label) : label}
            {asset?.exchangeDownloadURL && (
              <Tooltip title={t('layouts.import_ui_module_image_warning')} placement="top" arrow>
                <Warning sx={{ fontSize: 16, cursor: 'pointer', color: (theme) => theme.palette.warning.main }} />
              </Tooltip>
            )}
          </Typography>
        )}
        <div data-testid={assetBrowserTestIds.assetBrowserRoot} className={classes.elementRoot}>
          <div className={classes.root}>
            <div className={classes.inline}>
              {isLoadingAsset && (
                <AssetSkeleton data-testid={assetBrowserTestIds.loader} imageWidth={ASSET_CARD_SIZE} />
              )}
              {!isLoadingAsset && (
                <AssetCard
                  data-selected-card
                  asset={asset}
                  imageWidth={ASSET_CARD_SIZE}
                  clearable
                  hasError={hasError}
                  onClear={handleClear}
                  invalid={invalid}
                  useDarkBackground={useDarkBackground}
                  onEdit={setEditingAsset}
                />
              )}
              <Tooltip title={t('assets.browse')} placement="top" arrow>
                <div ref={popoverAnchorRef}>
                  <Button
                    data-testid={assetBrowserTestIds.assetBrowserBrowseButton}
                    color="grey"
                    onClick={handleBrowseClick}
                    loading={isLoading}
                  >
                    <ImageSearch />
                  </Button>
                </div>
              </Tooltip>
            </div>
            <Popover
              open={open}
              onClose={() => setOpen(false)}
              anchorEl={popoverAnchorRef.current}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <div className={classes.popoverContainer}>
                <TextField
                  data-testid={assetBrowserTestIds.assetBrowserFilter}
                  variant="outlined"
                  value={filter}
                  clearable
                  debounced
                  onChange={({ target: { value } }) => setFilter(value)}
                  placeholder={t('general.search')}
                  color={formControlColor}
                  fullWidth
                  inputRef={searchInputRef}
                />
                {isLoading ? (
                  <ShadowScroller loading>
                    <div className={classes.assetsContainer}>
                      <Repeat n={10}>
                        <AssetSkeleton imageWidth={ASSET_CARD_SIZE} />
                      </Repeat>
                    </div>
                  </ShadowScroller>
                ) : (
                  <ShadowList
                    data={assets}
                    endReached={loadNextPage}
                    itemContent={(index, data) => (
                      <ListItemButton
                        color="secondary"
                        key={data.id}
                        className={classes.assetRow}
                        onClick={() => handleAssetSelect(data)}
                        data-testid={assetBrowserTestIds.listItem}
                        data-filepath={data.filePath}
                      >
                        <AssetCard
                          data-card-index={index}
                          imageWidth={ASSET_CARD_SIZE}
                          asset={data}
                          useDarkBackground={useDarkBackground}
                          onEdit={setEditingAsset}
                        />
                      </ListItemButton>
                    )}
                  />
                )}
                <Button
                  data-testid={assetBrowserTestIds.assetBrowserUploadButton}
                  style={{ marginTop: 10 }}
                  onClick={handleUploadClick}
                  loading={isLoading}
                >
                  {t('assets.new_asset')}
                </Button>
              </div>
            </Popover>
          </div>
        </div>
      </div>

      <AssetUploader
        assetId={assetId}
        assetType={assetType}
        open={isUploadOpen}
        editingAsset={editingAsset}
        onAssetUpload={handleAssetUpload}
        acceptedFileTypes={acceptedFileTypes}
        onClose={handleAssetFormClose}
        aspectRatios={aspectRatios}
      />
    </>
  );
}

export const AssetBrowser = React.memo(AssetBrowserComponent);
